import React, { useState } from 'react';
import { services, images } from '../data/mock';
import { Users, Pill, MapPin, MessageCircle, FileText, Heart } from 'lucide-react';

const iconMap = {
  0: Pill,         // Medication Management - pill bottle
  1: MapPin,       // Healthcare Navigation - directions/navigation
  2: MessageCircle, // Medical Translation - communication
  3: FileText,      // Care Planning - documents/plans
  4: Heart,        // Emotional Support - heart/care
  5: Users         // Care Coordination - people/caregivers
};

const serviceDetails = [
  {
    title: "Medication Management",
    description: "Expert guidance organizing complex medication schedules, preventing dangerous interactions, and tracking side effects. We create simple systems to eliminate the 49% error rate caregivers experience."
  },
  {
    title: "Healthcare Navigation",
    description: "Navigate the fragmented healthcare system with confidence. We coordinate between multiple specialists, manage appointments, and ensure all providers communicate - solving the 36% communication barrier caregivers face."
  },
  {
    title: "Medical Translation & Advocacy",
    description: "Transform complex medical language into clear, actionable decisions. We speak up for your loved one in healthcare settings and ensure you understand all options - addressing the information gaps 53% of caregivers experience."
  },
  {
    title: "Care Coordination Planning",
    description: "Create comprehensive care plans that address multiple chronic conditions. We organize home health, equipment, and services while balancing quality of life with treatment needs."
  },
  {
    title: "Emotional Support & Resources",
    description: "Dedicated support for caregiver burnout, guilt, and anxiety. We connect you with resources and provide the emotional backing that prevents caregivers from becoming 'second-order patients'."
  },
  {
    title: "Care Coordination",
    description: "We don't replace your loved one's doctors. We help you navigate the healthcare system, understand your options, and make informed decisions."
  }
];

const ServiceCard = ({ service, detail, index }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const Icon = iconMap[index];

  return (
    <div 
      className="relative h-64 cursor-pointer preserve-3d transition-transform duration-700"
      style={{ 
        transformStyle: 'preserve-3d',
        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
      }}
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      {/* Front side - Title */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl p-6 border border-violet-100 backface-hidden flex flex-col items-center justify-center text-center"
        style={{ backfaceVisibility: 'hidden' }}
      >
        <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
          <Icon className="w-8 h-8 text-violet-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">{service.title}</h3>
        <p className="text-sm text-slate-700 font-medium">Hover to learn more</p>
      </div>

      {/* Back side - Description */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-2xl p-6 border border-violet-200 backface-hidden flex items-center justify-center text-center"
        style={{ 
          backfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)'
        }}
      >
        <div className="text-white">
          <h3 className="text-xl font-bold mb-3">{detail.title}</h3>
          <p className="text-base leading-relaxed font-medium">{detail.description}</p>
        </div>
      </div>
    </div>
  );
};

const ServicesSection = () => {
  return (
    <section id="services" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-fuchsia-500 font-semibold text-sm uppercase tracking-wide">
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mt-2 mb-4">
            How We <span className="text-nursebloc-gradient">Support Your Family</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Hover over any service below to learn how we help families navigate complex care situations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {serviceDetails.map((detail, index) => (
            <ServiceCard 
              key={index}
              service={services[index] || detail}
              detail={detail}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
